/** Content configuration for the guided "My Life Story" (ethical will) authoring experience.
 *
 * Chapters and prompts are ordered: the order here drives both the flow and the
 * chapter grouping in the keepsake PDF, so stories are always kept sorted by
 * this configuration before saving. Each prompt snapshots its chapter name and
 * question into the saved story so the backend can build the PDF without
 * duplicating this file.
 */

export const LIFE_STORY_CHAPTERS = [
  {
    key: 'origins',
    name: 'Where I Come From',
    description: 'Your beginnings, your family, the world you arrived into.',
    prompts: [
      {
        id: 'origins-born',
        question: 'Where were you born, and how would you describe the world when you arrived?',
        starters: ['I was born in…', 'Back then, the world was…', 'My family came from…']
      },
      {
        id: 'origins-memories',
        question: 'What are some of your earliest or favorite memories from your home town?',
        starters: ['The first thing I remember is…', 'Summers meant…', 'My favorite spot in town was…']
      },
      {
        id: 'origins-family',
        question: 'What was your family like when you were young?',
        starters: ['In our house…', 'Dinner at our house was…', 'My family was the kind of family that…']
      },
      {
        id: 'origins-community',
        question: 'What do you remember about your community (neighbors, friends, etc.)?',
        starters: ['Everyone on our street…', 'Our neighbors were…', 'After school, we would…']
      },
      {
        id: 'origins-influence',
        question: 'How has your hometown influenced the rest of your life?',
        starters: ['Growing up there taught me…', 'I carry my hometown with me…', 'To this day, I still…']
      }
    ]
  },
  {
    key: 'comingofage',
    name: 'Coming of Age',
    description: 'School days, big decisions, the person you were becoming.',
    prompts: [
      {
        id: 'comingofage-school',
        question: 'Where did you go to school, and what kind of a student were you?',
        starters: ['I went to school at…', 'My teachers would say I was…', 'My favorite subject was…']
      },
      {
        id: 'comingofage-teenager',
        question: 'How would you describe yourself as a teenager?',
        starters: ['As a teenager, I was…', 'My friends and I would…', 'Back then, I cared most about…']
      },
      {
        id: 'comingofage-no-longer-a-child',
        question: 'What was the moment you knew you were no longer a child?',
        starters: ['It happened when…', 'I remember realizing…', 'Everything changed the day…']
      },
      {
        id: 'comingofage-fears',
        question: 'What were your biggest fears as a child, and how did you overcome them?',
        starters: ['I was always afraid of…', 'What scared me most was…', 'I got past it by…']
      },
      {
        id: 'comingofage-setback',
        question: 'What was your first major setback, and how did it shape you?',
        starters: ['The first time life knocked me down…', 'At the time, it felt like…', 'Looking back…']
      }
    ]
  },
  {
    key: 'people',
    name: 'The People Who Shaped Me',
    description: 'Family, friends, and mentors who changed your path.',
    prompts: [
      {
        id: 'people-parents',
        question: 'What were your parents like?',
        starters: ['My mother was…', 'My father was…', 'Together, they…']
      },
      {
        id: 'people-friends',
        question: 'Who are some of your lifelong friends, and how has their friendship endured so long?',
        starters: ['We met when…', 'Through everything, we…', 'What keeps us close is…']
      },
      {
        id: 'people-siblings',
        question: 'How have your siblings influenced you?',
        starters: ['As kids, we…', 'They were the one who…', 'They taught me…']
      },
      {
        id: 'people-mentors',
        question: 'Have you ever had any coaches or mentors? How did their influence affect your growth and development?',
        starters: ['They saw something in me…', 'The best lesson they gave me…', 'We met when…']
      },
      {
        id: 'people-admire',
        question: 'Who do you look up to? And why do you admire them so much?',
        starters: ['I\u2019ve always looked up to…', 'What I admire most is…', 'They showed me…']
      }
    ]
  },
  {
    key: 'work',
    name: 'Work & Vocation',
    description: 'The work that defined your life, and what it taught you.',
    prompts: [
      {
        id: 'work-career',
        question: 'What is/was your career, and how did you decide on that path for yourself?',
        starters: ['I found my way to my work by…', 'I chose that path because…', 'My first day on the job…']
      },
      {
        id: 'work-lessons',
        question: 'What important life lessons did your career teach you?',
        starters: ['My work taught me…', 'It took years to learn…', 'The biggest lesson was…']
      },
      {
        id: 'work-twists',
        question: 'What unexpected twists and turns did your career take, and how did you handle them?',
        starters: ['I never expected…', 'The plan changed when…', 'I handled it by…']
      },
      {
        id: 'work-advice',
        question: 'What advice would you give someone who is entering the workforce right now?',
        starters: ['If I could tell my younger self…', 'The best advice I got was…', 'Never…']
      },
      {
        id: 'work-better-world',
        question: 'How did you use your career to make the world a better place?',
        starters: ['Through my work, I tried to…', 'I\u2019m proudest of…', 'In my own small way…']
      }
    ]
  },
  {
    key: 'joy',
    name: 'What Brings Me Joy',
    description: 'The people, places, and small pleasures that light you up.',
    prompts: [
      {
        id: 'joy-places',
        question: 'What are some of your favorite places in the world? Think about vacations, spots you go to get away from it all, favorite vistas, etc.',
        starters: ['My favorite place is…', 'I go there to…', 'The moment I arrive…']
      },
      {
        id: 'joy-doing',
        question: 'What are some things you love doing most in the world? No matter what else is going on, when you\u2019re doing this you\u2019re smiling ear to ear.',
        starters: ['I never get tired of…', 'I lose track of time when…', 'Nothing makes me smile like…']
      },
      {
        id: 'joy-favorites',
        question: 'What are some of your favorite things? Think about food, songs, artists, musicians, books, movies, TV shows, podcasts, etc. Jot down all of the fun things that make you happy, and why.',
        starters: ['It\u2019s the little things, like…', 'My favorite meal is…', 'I never say no to…']
      },
      {
        id: 'joy-jokes',
        question: 'What are some of your favorite jokes? What makes you laugh or brings a smile to your face?',
        starters: ['My favorite joke goes…', 'I laugh every time…', 'Nothing cracks me up like…']
      },
      {
        id: 'joy-passions',
        question: 'What are you passionate about? What could you talk about for hours and hours?',
        starters: ['I could talk for hours about…', 'People tease me about…', 'I\u2019ve loved it since…']
      }
    ]
  },
  {
    key: 'experiences',
    name: 'Experiences that Shaped Me',
    description: 'First loves, losses, and the moments that changed everything.',
    prompts: [
      {
        id: 'experiences-love',
        question: 'When did you fall in love for the first time? How did you know it was love?',
        starters: ['The first time I fell in love…', 'I knew it was love when…', 'We met when…']
      },
      {
        // The id predates TK-3052's copy change and stays put: saved stories
        // and skips are keyed by prompt id, so changing it would orphan them.
        id: 'experiences-hardship',
        question: 'When did you experience loss for the first time? How were you affected by that experience?',
        starters: ['The first loss I remember…', 'It changed me by…', 'What got me through was…']
      },
      {
        id: 'experiences-change',
        question: 'What are some moments in your life that you\u2019ve experienced major change? How did you adapt?',
        starters: ['Everything changed when…', 'At first, I…', 'I adapted by…']
      },
      {
        id: 'experiences-alive',
        question: 'What do you do for yourself that makes you feel alive? That you\u2019re living life to its fullest?',
        starters: ['I feel most alive when…', 'Nothing makes me feel more myself than…', 'I make time for…']
      },
      {
        id: 'experiences-awe',
        question: 'When did you first experience awe or wonder? What lasting impression has that moment left on you?',
        starters: ['I remember exactly where I was…', 'I couldn\u2019t believe my eyes…', 'It stayed with me because…']
      }
    ]
  },
  {
    key: 'values',
    name: 'What I Believe & What I\u2019ve Learned',
    description: 'The principles and convictions that guide your life.',
    prompts: [
      {
        id: 'values-faith',
        question: 'What role has faith played in your life? What words, if any, would you like to say about your faith?',
        starters: ['Faith has been…', 'What I believe is…', 'In hard times…']
      },
      {
        id: 'values-peace',
        question: 'What gives you peace?',
        starters: ['I find peace in…', 'When the world gets loud…', 'Nothing settles me like…']
      },
      {
        id: 'values-lessons',
        question: 'Looking back on your life, what are some of the most important lessons you\u2019ve learned?',
        starters: ['The most important lesson I\u2019ve learned…', 'It took me years to understand…', 'If life has taught me anything…']
      },
      {
        id: 'values-remembered',
        question: 'What values do you try to espouse in your life, and how would you like to be remembered?',
        starters: ['I try to live by…', 'What matters most to me is…', 'I hope to be remembered as…']
      },
      {
        id: 'values-meaning',
        question: 'What do you think is the meaning of life?',
        starters: ['To me, life is about…', 'I\u2019ve come to believe…', 'At the end of the day…']
      }
    ]
  },
  {
    key: 'hopes',
    name: 'Hopes for the Future',
    description: 'Your wishes for the people you love and the world you leave them.',
    prompts: [
      {
        id: 'hopes-children',
        question: 'What do you wish for your children and grandchildren?',
        starters: ['My greatest hope for you is…', 'I want you to know…', 'Promise me you\u2019ll…']
      },
      {
        id: 'hopes-family',
        question: 'How can your family love and understand each other better?',
        starters: ['What I hope for our family…', 'Love each other by…', 'Never forget to…']
      },
      {
        id: 'hopes-optimism',
        question: 'What sorts of things should we be hopeful or optimistic about?',
        starters: ['I\u2019m hopeful about…', 'The future looks bright because…', 'Have faith that…']
      },
      {
        id: 'hopes-encouragement',
        question: 'What words of encouragement do you have for future generations?',
        starters: ['To those who come after me…', 'Whatever happens, remember…', 'My advice for the road ahead…']
      },
      {
        id: 'hopes-world',
        question: 'How has the world changed in your lifetime, and how will it be even better in the generations to come?',
        starters: ['In my lifetime, the world…', 'I\u2019ve watched the world…', 'The generations to come will…']
      }
    ]
  },
  {
    key: 'own',
    name: 'A Chapter of Your Own',
    description: 'The stories that don\u2019t fit anywhere else — tell them your way.',
    prompts: [
      {
        id: 'own-missed',
        question: 'What did we miss?',
        starters: ['One thing we haven\u2019t covered…', 'Something you should know…', 'I almost forgot to mention…']
      },
      {
        id: 'own-stories',
        question: 'What other stories or experiences would you like to share?',
        starters: ['There\u2019s one more story…', 'I always meant to tell you…', 'You might not know this, but…']
      },
      {
        id: 'own-final-words',
        question: 'What final words would you like to share with your loved ones?',
        starters: ['If these are my last words to you…', 'More than anything…', 'What I want to leave you with…']
      },
      {
        id: 'own-sum-up',
        question: 'How would you sum up your life?',
        starters: ['If I had to sum it all up…', 'My life has been…', 'All in all…']
      },
      {
        id: 'own-why',
        question: 'Why are you taking the time to write your story?',
        starters: ['I\u2019m writing this because…', 'I want you to have…', 'My hope for this story is…']
      }
    ]
  }
]

/**
 * Rest-screen headlines; {c} is replaced with the chapter name (mock's
 * REST_LINES). None of these may mention how many stories were added — the
 * rest screen can come up after fewer than a full sitting (TK-2983 AC 4.9).
 */
export const REST_LINES = [
  'The pages of {c} are filling up beautifully.',
  '{c} is coming to life.',
  'More of {c}, kept for good.',
  '{c} is taking shape.',
  'You\u2019re building something lasting in {c}.',
  '{c} is starting to sound like you.',
  'Every word here becomes part of {c}.',
  'You\u2019ve added real warmth to {c}.',
  '{c} is growing into something worth keeping.',
  'Piece by piece, {c} is coming together.'
]

/**
 * Ice-breakers for the evergreen open question (mock's evergreenFor starters).
 */
export const EVERGREEN_STARTERS = [
  'One more thing\u2026',
  'While I remember it\u2026',
  'I never told anyone this, but\u2026'
]

const EVERGREEN_PROMPT_ID_PREFIX = 'more:'

/**
 * Every chapter ends in an open question — it never runs dry (mock's
 * evergreenFor). Once a chapter's configured prompts are used up, "Is there
 * anything else…" keeps it open, and it can be answered again and again: each
 * answer mints a fresh prompt id so it saves as a new story rather than
 * replacing the last one.
 */
export const newEvergreenPromptId = chapterKey =>
  `${EVERGREEN_PROMPT_ID_PREFIX}${chapterKey}:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

/** The chapter key embedded in an evergreen prompt id, or null for configured prompts. */
export const evergreenChapterKey = promptId => (
  promptId && promptId.startsWith(EVERGREEN_PROMPT_ID_PREFIX) ? promptId.split(':')[1] : null
)

export const isEvergreenPromptId = promptId => Boolean(evergreenChapterKey(promptId))

export const evergreenPrompt = ({chapter, promptId}) => ({
  id: promptId,
  question: `Is there anything else about ${chapter.name} you\u2019d like to add?`,
  starters: EVERGREEN_STARTERS
})

export const STUCK_TIPS = [
  'Tell it the way you\u2019d tell it at the kitchen table — not the way you\u2019d write it.',
  'Start with one small, true detail: a smell, a song, a street name. The rest follows.',
  'Say it out loud instead. Talking is easier than writing, and we\u2019ll capture every word.',
  'Leave it blank and press Next. There\u2019s no score being kept — another question will suit you better.'
]

export const findChapter = chapterKey => LIFE_STORY_CHAPTERS.find(chapter => chapter.key === chapterKey)

export const findPrompt = promptId => {
  for (const chapter of LIFE_STORY_CHAPTERS) {
    const prompt = chapter.prompts.find(chapterPrompt => chapterPrompt.id === promptId)

    if (prompt)
      return {chapter, prompt}
  }

  return null
}
